import "./columnPin.css";
import { persistFromDirective } from "../columnSettings/columnSettings";

var DEFAULT_LOCKED = ["actions", "row", "product"];
var DEFAULT_ANCHOR = "product";

function translateLabel(vnode, key, fallback) {
  try {
    if (vnode && vnode.context && typeof vnode.context.$t === "function") {
      var translated = vnode.context.$t(key);
      if (translated && translated !== key) return translated;
    }
  } catch (e) {
    // ignore i18n errors and use fallback
  }
  return fallback;
}

function parseOptions(binding) {
  var value = binding && binding.value;
  if (!value) {
    return {
      headers: null,
      visibleHeaders: null,
      locked: DEFAULT_LOCKED.slice(),
      anchor: DEFAULT_ANCHOR,
    };
  }
  if (Array.isArray(value)) {
    return {
      headers: value,
      visibleHeaders: value,
      locked: DEFAULT_LOCKED.slice(),
      anchor: DEFAULT_ANCHOR,
    };
  }
  return {
    headers: value.headers || null,
    visibleHeaders: value.visibleHeaders || value.headers || null,
    locked: Array.isArray(value.locked) ? value.locked.slice() : DEFAULT_LOCKED.slice(),
    anchor: value.anchor || DEFAULT_ANCHOR,
  };
}

function ensureMeta(headers) {
  if (!Array.isArray(headers)) return;
  headers.forEach(function (header, index) {
    if (!header) return;
    if (header.originalIndex == null) {
      header.originalIndex = index;
    }
    if (header.pinned == null) {
      header.pinned = false;
    }
    if (header.pinOrder == null) {
      header.pinOrder = null;
    }
  });
}

function isLocked(header, locked) {
  return !!(header && locked.indexOf(header.value) !== -1);
}

function reorderHeaders(headers, locked, anchor) {
  if (!Array.isArray(headers) || !headers.length) return headers;

  ensureMeta(headers);

  var lockedHeaders = [];
  var pinnedHeaders = [];
  var restHeaders = [];

  headers.forEach(function (header) {
    if (!header) return;
    if (isLocked(header, locked)) {
      lockedHeaders.push(header);
      return;
    }
    if (header.pinned) {
      pinnedHeaders.push(header);
      return;
    }
    restHeaders.push(header);
  });

  lockedHeaders.sort(function (a, b) {
    return (a.originalIndex || 0) - (b.originalIndex || 0);
  });
  pinnedHeaders.sort(function (a, b) {
    return (a.pinOrder || 0) - (b.pinOrder || 0);
  });
  restHeaders.sort(function (a, b) {
    return (a.originalIndex || 0) - (b.originalIndex || 0);
  });

  // keep locked block ending with anchor when possible
  var anchorIndex = lockedHeaders.findIndex(function (header) {
    return header.value === anchor;
  });
  if (anchorIndex > -1 && anchorIndex !== lockedHeaders.length - 1) {
    var anchorHeader = lockedHeaders.splice(anchorIndex, 1)[0];
    lockedHeaders.push(anchorHeader);
  }

  var next = lockedHeaders.concat(pinnedHeaders, restHeaders);
  headers.splice.apply(headers, [0, headers.length].concat(next));
  return headers;
}

function nextPinOrder(headers) {
  var max = 0;
  headers.forEach(function (header) {
    if (header && header.pinned && Number(header.pinOrder) > max) {
      max = Number(header.pinOrder);
    }
  });
  return max + 1;
}

function compactPinOrders(headers) {
  var pinned = headers
    .filter(function (header) {
      return header && header.pinned;
    })
    .sort(function (a, b) {
      return (a.pinOrder || 0) - (b.pinOrder || 0);
    });
  pinned.forEach(function (header, index) {
    header.pinOrder = index + 1;
  });
}

function getTable(el) {
  if (!el) return null;
  if (el.tagName === "TABLE") return el;
  return el.querySelector("table");
}

// Sticky headers carry inline !important z-index/overflow, which a
// stylesheet :hover rule cannot override. Raise the header inline while
// its tooltip is open and restore the previous values afterwards.
function raiseHeaderForTooltip(btn) {
  var th = btn && btn.closest ? btn.closest("th") : null;
  if (!th) return;
  btn.__tooltipHeaderRestore = {
    zIndex: th.style.getPropertyValue("z-index"),
    zIndexPriority: th.style.getPropertyPriority("z-index"),
    overflow: th.style.getPropertyValue("overflow"),
    overflowPriority: th.style.getPropertyPriority("overflow"),
  };
  th.style.setProperty("z-index", "60", "important");
  th.style.setProperty("overflow", "visible", "important");
}

function restoreHeaderForTooltip(btn) {
  var th = btn && btn.closest ? btn.closest("th") : null;
  var saved = btn && btn.__tooltipHeaderRestore;
  if (!th || !saved) return;
  if (saved.zIndex) {
    th.style.setProperty("z-index", saved.zIndex, saved.zIndexPriority || "");
  } else {
    th.style.removeProperty("z-index");
  }
  if (saved.overflow) {
    th.style.setProperty("overflow", saved.overflow, saved.overflowPriority || "");
  } else {
    th.style.removeProperty("overflow");
  }
  btn.__tooltipHeaderRestore = null;
}

function isRtl(el) {
  var root =
    (el && el.closest && el.closest(".v-application")) ||
    document.body ||
    document.documentElement;
  var dir =
    (root && root.getAttribute && root.getAttribute("dir")) ||
    document.documentElement.getAttribute("dir") ||
    getComputedStyle(root || document.documentElement).direction;
  return dir === "rtl";
}

function resolveVisibleHeaders(options) {
  if (Array.isArray(options.visibleHeaders)) return options.visibleHeaders;
  return options.headers || [];
}

function findHeaderByValue(headers, value) {
  if (!Array.isArray(headers)) return null;
  return (
    headers.find(function (header) {
      return header && header.value === value;
    }) || null
  );
}

function togglePin(options, columnValue) {
  var headers = options.headers;
  if (!Array.isArray(headers)) return false;
  ensureMeta(headers);

  var header = findHeaderByValue(headers, columnValue);
  if (!header || isLocked(header, options.locked)) return false;

  if (header.pinned) {
    header.pinned = false;
    header.pinOrder = null;
    compactPinOrders(headers);
  } else {
    header.pinned = true;
    header.pinOrder = nextPinOrder(headers);
  }

  reorderHeaders(headers, options.locked, options.anchor);
  return true;
}

function clearStickyInlineStyles(node) {
  if (!node) return;
  node.classList.remove("is-sticky");
  node.style.removeProperty("left");
  node.style.removeProperty("right");
  node.style.removeProperty("z-index");
  node.style.removeProperty("position");
  node.style.removeProperty("top");
  node.style.removeProperty("background-color");
}

function applyStickyToNode(node, side, offset, zIndex, width, isHeader) {
  if (!node) return;
  node.classList.add("is-sticky");
  if (isHeader) {
    node.classList.add("column-pin-th");
  } else {
    node.classList.add("column-pin-cell");
  }
  node.style.setProperty("position", "sticky", "important");
  if (isHeader) {
    node.style.setProperty("top", "0", "important");
  }
  node.style.setProperty(side, offset + "px", "important");
  node.style.setProperty(side === "right" ? "left" : "right", "auto", "important");
  node.style.setProperty("z-index", String(zIndex), "important");
  node.style.setProperty("overflow", "hidden", "important");
  if (isHeader) {
    node.style.setProperty("background-color", "#fff", "important");
  }
  if (width > 0) {
    node.style.width = width + "px";
    node.style.minWidth = width + "px";
    node.style.maxWidth = width + "px";
  }
}

function applyStickyStyles(el, options) {
  var table = getTable(el);
  if (!table) return;

  var rtl = isRtl(el);
  var side = rtl ? "right" : "left";
  var visible = resolveVisibleHeaders(options);
  var locked = options.locked || [];
  var ths = Array.prototype.slice.call(
    table.querySelectorAll("thead tr:not(.v-data-table__progress) > *")
  );
  var rows = Array.prototype.slice.call(table.querySelectorAll("tbody tr"));
  var stickyItems = [];
  var offset = 0;

  ths.forEach(function (th, index) {
    clearStickyInlineStyles(th);
    th.removeAttribute("data-col");
    var header = visible[index];
    if (!header) return;
    th.setAttribute("data-col", header.value);

    rows.forEach(function (row) {
      clearStickyInlineStyles(row.children[index]);
      if (row.children[index]) {
        row.children[index].setAttribute("data-col", header.value);
      }
    });

    var sticky = isLocked(header, locked) || !!header.pinned;
    if (!sticky) return;

    var width = th.offsetWidth || Number(header.width) || 100;
    if (header.width !== width) {
      header.width = width;
    }

    stickyItems.push({
      index: index,
      th: th,
      header: header,
      width: width,
      offset: offset,
    });
    offset += width;
  });

  var totalSticky = stickyItems.length;
  stickyItems.forEach(function (item, stickyIndex) {
    // closer-to-edge sticky columns must stay above later ones.
    // Header base 6+ stays above fixed-header th (z-index: 2) so
    // scrolling headers cannot paint over pinned ones. Safe with
    // wrapper isolation:isolate (reload overlays stay on top).
    var headerZ = 6 + (totalSticky - stickyIndex);
    var cellZ = 3 + (totalSticky - stickyIndex);
    applyStickyToNode(item.th, side, item.offset, headerZ, item.width, true);

    if (item.header && item.header.value === "product") {
      item.th.style.setProperty("border-left", "1px solid #e0e0e0", "important");
    }

    rows.forEach(function (row) {
      applyStickyToNode(
        row.children[item.index],
        side,
        item.offset,
        cellZ,
        item.width,
        false
      );
    });
  });
}

function clearPinButtons(table) {
  if (!table) return;
  table.querySelectorAll(".column-pin-btn").forEach(function (btn) {
    btn.remove();
  });
}

function pinStateSignature(visible) {
  if (!Array.isArray(visible)) return "";
  return visible
    .map(function (header) {
      if (!header) return "";
      return [header.value, header.pinned ? "1" : "0", header.pinOrder || 0].join(":");
    })
    .join("|");
}

function bindPinButtons(el, binding) {
  var options = parseOptions(el.__columnPinBinding || binding);
  var table = getTable(el);
  if (!table || !Array.isArray(options.headers)) return;

  ensureMeta(options.headers);
  var visible = resolveVisibleHeaders(options);
  var signature = pinStateSignature(visible);

  if (
    el.__columnPinBound &&
    el.__columnPinSignature === signature &&
    table.querySelector(".column-pin-btn, .column-pin-th")
  ) {
    applyStickyStyles(el, options);
    return;
  }

  clearPinButtons(table);

  var ths = table.querySelectorAll("thead tr:not(.v-data-table__progress) > *");

  ths.forEach(function (th, index) {
    var header = visible[index];
    if (!header) return;

    th.classList.add("column-pin-th");

    if (isLocked(header, options.locked)) {
      return;
    }

    var columnValue = header.value;
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "column-pin-btn" + (header.pinned ? " is-pinned" : "");
    btn.setAttribute(
      "data-tooltip",
      translateLabel(
        el.__columnPinVnode,
        header.pinned ? "unpin-column" : "pin-column",
        header.pinned ? "برداشتن ثابت ستون" : "ثابت کردن ستون"
      )
    );

    var icon = document.createElement("i");
    icon.className = "mdi " + (header.pinned ? "mdi-pin" : "mdi-pin-outline");
    icon.setAttribute("aria-hidden", "true");
    btn.appendChild(icon);

    btn.addEventListener("mouseenter", function () {
      raiseHeaderForTooltip(btn);
    });
    btn.addEventListener("mouseleave", function () {
      restoreHeaderForTooltip(btn);
    });

    btn.addEventListener("click", function (event) {
      restoreHeaderForTooltip(btn);
      event.preventDefault();
      event.stopPropagation();
      var latest = parseOptions(el.__columnPinBinding || binding);
      var changed = togglePin(latest, columnValue);
      if (!changed) return;
      el.__columnPinSignature = null;
      var vnode = el.__columnPinVnode;
      if (vnode && vnode.context && typeof vnode.context.$forceUpdate === "function") {
        vnode.context.$forceUpdate();
      }
      persistFromDirective(vnode, el.__columnPinBinding || binding, latest.headers);
      scheduleBind(el, el.__columnPinBinding || binding, vnode);
    });

    // keep order in RTL: drag (right) -> pin -> title
    var dragBtn = th.querySelector(".column-drag-btn");
    if (dragBtn && dragBtn.nextSibling) {
      th.insertBefore(btn, dragBtn.nextSibling);
    } else if (dragBtn) {
      th.appendChild(btn);
    } else if (th.firstChild) {
      th.insertBefore(btn, th.firstChild);
    } else {
      th.appendChild(btn);
    }
  });

  applyStickyStyles(el, options);
  el.__columnPinBound = true;
  el.__columnPinSignature = signature;
}

function scheduleBind(el, binding, vnode) {
  el.__columnPinBinding = binding;
  el.__columnPinVnode = vnode;
  if (el.__columnPinTimer) {
    clearTimeout(el.__columnPinTimer);
  }
  el.__columnPinTimer = setTimeout(function () {
    bindPinButtons(el, binding);
  }, 0);
}

export function getStickyStyleForColumn(columns, columnValue, locked, rtl) {
  var list = Array.isArray(columns) ? columns : [];
  var lockedValues = Array.isArray(locked) ? locked : DEFAULT_LOCKED;
  var offset = 0;
  var stickyIndexes = [];

  for (var i = 0; i < list.length; i++) {
    var col = list[i];
    if (!col) continue;
    if (lockedValues.indexOf(col.value) !== -1 || !!col.pinned) {
      stickyIndexes.push(i);
    }
  }

  for (var j = 0; j < list.length; j++) {
    var current = list[j];
    if (!current) continue;
    var sticky = lockedValues.indexOf(current.value) !== -1 || !!current.pinned;
    if (current.value === columnValue) {
      if (!sticky) return null;
      var stickyOrder = stickyIndexes.indexOf(j);
      var style = {
        position: "sticky",
        zIndex: 3 + (stickyIndexes.length - stickyOrder),
        overflow: "hidden",
      };
      if (rtl) {
        style.right = offset + "px";
        style.left = "auto";
      } else {
        style.left = offset + "px";
        style.right = "auto";
      }
      if (current.width) {
        style.width = current.width + "px";
        style.minWidth = current.width + "px";
        style.maxWidth = current.width + "px";
      }
      return style;
    }
    if (sticky) {
      offset += Number(current.width) || 100;
    }
  }
  return null;
}

export function reorderPinnedHeaders(headers, options) {
  var opts = options || {};
  return reorderHeaders(
    headers,
    opts.locked || DEFAULT_LOCKED,
    opts.anchor || DEFAULT_ANCHOR
  );
}

var ColumnPinDirective = {
  inserted: function (el, binding, vnode) {
    scheduleBind(el, binding, vnode);
  },
  componentUpdated: function (el, binding, vnode) {
    scheduleBind(el, binding, vnode);
  },
  unbind: function (el) {
    if (el.__columnPinTimer) {
      clearTimeout(el.__columnPinTimer);
      el.__columnPinTimer = null;
    }
    clearPinButtons(getTable(el));
  },
};

export default {
  install: function (Vue) {
    Vue.directive("column-pin", ColumnPinDirective);
  },
};
